import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
// iter114d: useAuth + UserDropdown imports removed — auth widget
// now lives exclusively in TopNav.

const ProductDetail = () => {
  const { productId } = useParams();
  // iter114d: removed `const { isAuthenticated } = useAuth();` — no
  // longer needed; auth widget is in TopNav.

  const productDetails = {
    'stickers': {
      name: 'Custom Stickers',
      tagline: 'Make Your Brand Stick',
      description: 'Create awesome promotional campaigns with our large range of custom stickers and labels. From die-cut singles to rolls, we have the perfect solution for every need.',
      image: '/images/categories/Stickers.png',
      features: [
        'Pages (Single & Multiple Design)',
        'Die-Cut Singles - Perfect for individual stickers',
        'Kiss-Cut Singles - Easy peel application',
        'Rolls - Ideal for high-volume applications',
        'Multiple materials: Vinyl, Paper, Clear, Holographic',
        'Finishes: Matte, Gloss, UV coating'
      ],
      applications: [
        'Product packaging and labels',
        'Promotional giveaways',
        'Brand merchandise',
        'Event marketing',
        'Window displays'
      ]
    },
    'labels': {
      name: 'Product Labels',
      tagline: 'Label with Confidence',
      description: 'Your product label is your most important marketing tool. Choose from an extensive selection of materials and finishes to make your product stand out on the shelf.',
      image: '/images/categories/Labels.png',
      features: [
        'Label Unwind Direction (for machine application)',
        'Leading Edge Positioning',
        'Custom sizes and shapes',
        'Waterproof and weatherproof options',
        'FDA approved materials available',
        'Variable data printing capability'
      ],
      applications: [
        'Food & beverage products',
        'Cosmetics and beauty products',
        'Industrial products',
        'Retail packaging',
        'Shipping and logistics'
      ]
    },
    'decals': {
      name: 'Custom Decals',
      tagline: 'Durable Graphics That Last',
      description: 'High-quality decals for indoor and outdoor use. Perfect for vehicles, windows, walls, and more.',
      image: '/images/categories/Decals_and_Clings/Decals_and_Clings.png',
      features: [
        'Indoor and outdoor durability',
        'UV resistant materials',
        'Removable and permanent options',
        'Weather resistant',
        'Custom shapes and sizes',
        'White or clear backgrounds'
      ],
      applications: [
        'Vehicle graphics',
        'Window displays',
        'Wall decor',
        'Equipment labeling',
        'Promotional displays'
      ]
    },
    'clings': {
      name: 'Window Clings',
      tagline: 'Stick Without the Mess',
      description: 'Static cling decals that stick without adhesive. Perfect for temporary displays and promotions.',
      image: '/images/categories/Decals_and_Clings/Decals_and_Clings.png',
      features: [
        'No adhesive required',
        'Reusable and repositionable',
        'Easy to apply and remove',
        'Clear or white material',
        'Indoor use',
        'Perfect for glass surfaces'
      ],
      applications: [
        'Retail window displays',
        'Seasonal promotions',
        'Office glass partitions',
        'Temporary signage',
        'Event marketing'
      ]
    },
    'iron-ons': {
      name: 'DTF Heat Transfers',
      tagline: 'Transfer Your Imagination',
      description: 'High-quality DTF (Direct to Film) heat transfers for apparel and textiles. Vibrant colors and exceptional durability.',
      image: '/images/categories/Wearables.png',
      features: [
        'Works on cotton, polyester, blends',
        'Full color printing',
        'Soft hand feel',
        'Excellent wash durability',
        'No weeding required',
        'Quick turnaround times'
      ],
      applications: [
        'Custom t-shirts',
        'Team uniforms',
        'Promotional apparel',
        'Fashion garments',
        'Bag customization'
      ]
    },
    'patches': {
      name: 'Custom Patches',
      tagline: 'Wearable Art',
      description: 'Create custom embroidered or printed patches for any application.',
      image: '/images/categories/Wearables.png',
      features: [
        'Embroidered or printed options',
        'Iron-on or sew-on backing',
        'Custom shapes and sizes',
        'Merrowed or heat cut borders',
        'Up to 90% thread coverage',
        'Durable and long-lasting'
      ],
      applications: [
        'Uniforms and workwear',
        'Team sports',
        'Military and tactical gear',
        'Fashion accessories',
        'Promotional merchandise'
      ]
    },
    'magnets': {
      name: 'Custom Magnets',
      tagline: 'Stick Around',
      description: 'Make your brand stick with custom magnets. Perfect for vehicles, refrigerators, and metal surfaces.',
      image: '/images/categories/Unique_Custom_Products/Unique_Custom_Products.png',
      features: [
        '30mil magnetic material',
        'Indoor and outdoor use',
        'UV resistant printing',
        'Custom shapes available',
        'Strong magnetic hold',
        'Vehicle safe'
      ],
      applications: [
        'Vehicle signage',
        'Refrigerator magnets',
        'Business cards',
        'Promotional giveaways',
        'Trade show displays'
      ]
    },
    'badges': {
      name: 'Custom Badges',
      tagline: 'Identity Solutions',
      description: 'Professional badges and name tags for events, conferences, and identification.',
      image: '/images/categories/Unique_Custom_Products/Unique_Custom_Products.png',
      features: [
        'Multiple fastener options',
        'Full color printing',
        'Various sizes available',
        'Durable materials',
        'Quick turnaround',
        'Bulk pricing available'
      ],
      applications: [
        'Event identification',
        'Conference badges',
        'Name tags',
        'Security badges',
        'Membership identification'
      ]
    }
  };

  // Handle product ID aliases
  const productIdMap = {
    'dtf-transfers': 'iron-ons'  // DTF Heat Transfers alias
  };

  const actualProductId = productIdMap[productId] || productId;
  const product = productDetails[actualProductId];

  // Helper function to generate quote URL with correct product mapping
  const getQuoteUrl = () => {
    // Map ProductDetail IDs to QuoteCalculator product types
    const productToQuoteMap = {
      'stickers': 'stickers',
      'labels': 'labels',
      'decals': 'decals',
      'clings': 'decals',
      'iron-ons': 'iron-ons',
      'patches': 'iron-ons',
      'magnets': 'magnets',
      'badges': 'magnets'
    };

    // Use the actual product ID (after alias resolution) to determine quote product type
    const quoteProductType = productToQuoteMap[actualProductId] || 'stickers';

    // Pass the original productId as the format for proper custom quote detection
    return `/quote?product=${quoteProductType}&format=${productId}&step=3`;
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Product Not Found</h1>
          <Link to="/products">
            <Button className="bg-[#6FBF73] hover:bg-[#4A7C59] text-white">
              Back to Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* iter114s.44 — Page-level brand band removed for the same
          reason as SubcategoryDetail: TopNav + breadcrumb strip
          already carry the navigation context, so a third stacked
          header here was redundant chrome. */}

      <div className="max-w-7xl mx-auto px-6 py-12">
        <Link to="/products" className="inline-flex items-center text-[#6FBF73] hover:text-[#4A7C59] mb-8">
          ← Back to All Products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src={product.image}
              alt={product.name}
              className="w-full rounded-2xl shadow-2xl"
            />
          </div>

          <div>
            <h1 className="text-5xl font-bold text-gray-800 mb-4">{product.name}</h1>
            <p className="text-2xl text-[#6FBF73] font-semibold mb-6">{product.tagline}</p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">{product.description}</p>
            <Link to={getQuoteUrl()}>
              <Button size="lg" className="bg-[#6FBF73] hover:bg-[#4A7C59] text-lg px-8 py-6">
                Get Instant Quote <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Features & Options</h2>
            <ul className="space-y-3">
              {product.features.map((feature, index) => (
                <li key={`feature-${feature}-${index}`} className="flex items-start gap-3">
                  <span className="text-[#6FBF73] text-xl">✓</span>
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Perfect For</h2>
            <ul className="space-y-3">
              {product.applications.map((application, index) => (
                <li key={`app-${application}-${index}`} className="flex items-start gap-3">
                  <span className="text-[#6FBF73] text-xl">•</span>
                  <span className="text-gray-700">{application}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#4A7C59] text-white pt-12 pb-16 mt-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <img
                src="/eg3-logo.png"
                alt="Evergreen Graphics Group"
                className="h-24 w-24 mb-4 rounded-full"
                style={{ objectFit: 'contain' }}
              />
              <p className="text-green-100">Professional print solutions for your business needs.</p>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Contact Us</h3>
              <p className="text-green-100">Email: jfishburn@eg3.ca</p>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/products" className="text-green-100 hover:text-white">Our Products</Link></li>
                <li><Link to="/quote" className="text-green-100 hover:text-white">Get a Quote</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-green-600 mt-8 pt-8 text-center text-green-100">
            <p>&copy; 2026 Evergreen Graphics Group Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProductDetail;
